"use client";

import React from "react";
import { Compass, CircleDollarSign, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DecisionItem {
  title: string;
  detail: string;
}
const designDecisions: DecisionItem[] = [{
  title: "Space type",
  detail: "Screened porch, covered pavilion, open-air stone patio, or multi-level deck configured for your yard's slope."
}, {
  title: "Decking and patio materials",
  detail: "Capped composite, natural hardwood, bluestone pavers, or poured architectural concrete surfaces."
}, {
  title: "Screening and covering design",
  detail: "Integrated motorized drop screens, vaulted beadboard ceilings, skylights, and custom timber post construction."
}, {
  title: "Utility runs for outdoor kitchens",
  detail: "Dedicated gas lines, high-amperage electrical panels, water supply lines, and drainage planning."
}, {
  title: "Landscaping integration",
  detail: "Retaining walls, privacy buffers, grade adjustments, and perimeter drainage tied into existing property lines."
}];
const costDrivingFactors: DecisionItem[] = [{
  title: "Square footage",
  detail: "The total footprint of functional usable area directly affects excavation, framing lumber, and surface materials."
}, {
  title: "Structural covering type",
  detail: "Freestanding pergolas versus complex roof tie-ins matching your home's existing rooflines and architectural loads."
}, {
  title: "Utility runs",
  detail: "Distance from your interior mechanical room to the outdoor cooking island for water, sewer, power, and gas."
}, {
  title: "Material grade",
  detail: "Standard dimensional framing versus marine-grade stainless cabinetry, natural stone masonry, and composite grades."
}, {
  title: "Decking material choice",
  detail: "Pressure-treated pine, premium capped composites, or exotic hardwoods requiring specialized fastening systems."
}];
export default function PlanningDecisions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="outdoorlivingplanningdecisions" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-opacity duration-700 ease-out">
          {/* Header */}
          <div className="max-w-3xl mb-14 lg:mb-16">
            <Badge variant="outline" className="mb-3 text-xs tracking-wide uppercase">
              Project Feasibility
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
              Planning your outdoor living renovation
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Every outdoor addition in Brookhaven involves balancing space function with site realities. Reviewing material priorities and investment drivers early helps shape a realistic construction roadmap.
            </p>
          </div>

          {/* Main Grid: Decisions List & Cost Drivers */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start mb-14">
            {/* Card 1: Key Design Decisions */}
            <Card className="lg:col-span-6 border-border shadow-sm bg-card">
              <CardHeader className="border-b border-border pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-primary/15 text-primary-foreground flex items-center justify-center">
                    <Compass className="size-5 text-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-serif font-medium text-foreground">
                      Design and material decisions
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Core structural and architectural choices homeowners evaluate
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {designDecisions.map((item, index) => <li key={item.title} data-index={index} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground block">
                          {item.title}
                        </span>
                        <span className="text-muted-foreground leading-normal">
                          {item.detail}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Card 2: Cost-Driving Factors */}
            <Card className="lg:col-span-6 border-border shadow-sm bg-card">
              <CardHeader className="border-b border-border pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
                    <CircleDollarSign className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-serif font-medium text-foreground">
                      Cost-driving factors
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Site conditions and specification choices that shape the budget
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {costDrivingFactors.map((item, index) => <li key={item.title} data-index={index} className="flex items-start gap-3 text-sm">
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground mt-0.5">
                        {index + 1}
                      </span>
                      <div>
                        <span className="font-semibold text-foreground block">
                          {item.title}
                        </span>
                        <span className="text-muted-foreground leading-normal">
                          {item.detail}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Photo Context Duo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch pt-2">
            <div className="relative overflow-hidden rounded-lg border border-border group bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_planning_patio.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_planning_patio.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_planning_patio.png-wvc-sizes" alt="Covered stone patio with custom timber ceiling and outdoor fireplace" loading="lazy" decoding="async" className="w-full h-72 sm:h-80 object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                <p className="text-white text-sm font-semibold">
                  Structural porch additions
                </p>
                <p className="text-white/80 text-xs mt-1">
                  Tie-in roofline framing matched to existing Brookhaven residence architecture.
                </p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-lg border border-border group bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_kitchen_pergola.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_kitchen_pergola.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_kitchen_pergola.png-wvc-sizes" alt="Custom built outdoor kitchen island with granite countertops and stainless appliances" loading="lazy" decoding="async" className="w-full h-72 sm:h-80 object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                <p className="text-white text-sm font-semibold">
                  Utility-integrated kitchen zones
                </p>
                <p className="text-white/80 text-xs mt-1">
                  Dedicated utility trenching for gas grills, prep sinks, and outdoor bar seating.
                </p>
              </div>
            </div>
          </div>

          {/* Action Row */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-card rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">
                Ready to review your property conditions with our design team?
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                We assess elevation, setbacks, and existing utility locations during your on-site consultation.
              </p>
            </div>
            <Button asChild variant="default" size="default" className="w-full sm:w-auto">
              <Link to="/ContactRequestAnEstimatePage">
                Schedule an on-site consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}